import { RefreshCw } from 'lucide-react';
import { hasScope } from '@nyce/admin-kit/api';
import { Button } from '@nyce/admin-kit/ui';
import { SCOPES } from '../../api';
import { EmptyState, ErrorBox, Page } from '../../components/common';
import type { HubLogin } from '../../session';
import { IssueList } from './shared';
import { Clusters, Databases, Environments, Jobs, OpsKpis, opsIssues, useHubStatus } from './ops';
import { useEnvironmentIncidents } from './stats';

/** Operations dashboard: every environment, database, job and cluster in detail. */
export function OperationsDashboardPage({ login }: { login: HubLogin }) {
  const canPods = hasScope(login.scopes, SCOPES.podsRead);
  const canDb = hasScope(login.scopes, SCOPES.dbRead);
  const res = useHubStatus(canPods || canDb);
  const s = res.data;
  const incidents = useEnvironmentIncidents(hasScope(login.scopes, SCOPES.statsRead));
  return (
    <Page
      title="Operations"
      description="Environments, databases, backup/copy jobs and GKE clusters. Refreshes every 30 s."
      actions={
        <>
          {s && (
            <span className="nyk-ui-hint" data-testid="status-updated">
              Updated {new Date(s.generatedAt).toLocaleTimeString()}
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={res.loading}
            onClick={() => void res.reload()}
          >
            Refresh
          </Button>
        </>
      }
    >
      <ErrorBox error={res.error} />
      {!s && res.loading && <div className="oph-dash-skeleton" aria-busy="true" />}
      {!canPods && !canDb && (
        <EmptyState card title="No operations access" hint="Needs ophub:pods:read or ophub:db:read." />
      )}
      {s && (
        <div className={res.loading ? 'oph-dash-stale' : undefined}>
          <OpsKpis status={s} />
          <IssueList issues={[...opsIssues(s), ...incidents]} />
          {s.environments && <Environments envs={s.environments} />}
          {s.databases && <Databases dbs={s.databases} isAdmin={hasScope(login.scopes, SCOPES.admin)} />}
          {s.jobs && <Jobs jobs={s.jobs} />}
          {s.clusters && <Clusters status={s} />}
        </div>
      )}
    </Page>
  );
}
